import { PERMISSIONS } from 'api/auth/constants';
import {
  API_ACCESS_MODE,
  CALCULATION_VARIANTS,
  COMPARISON_TYPES,
  DEVIATION_TYPES,
  NOTIFICATION_TYPES,
  PERIOD_TYPE_VARIANTS,
  SETTLEMENT_TYPE,
} from 'api/data/constants';
import {
  IAnalyticDictionaries,
  IAuthDictionaries,
  IBuilderDictionaries,
  IDictionaries,
  INotificationFilters,
  IReconFilters,
  IResultCode,
  IRole,
  TAnalyticDictionaryType,
  TDictionaryType,
} from 'api/data/types';
import { ORGANIZATIONS_STATUS, ORGANIZATIONS_TYPE } from 'api/organizations/types';
import {
  FEE_MODE,
  SETTLEMENT_CONTRACT_PERIOD_TYPE,
} from 'api/settlementContracts/constants';
import {
  CHARGEBACK_STATE,
  EXIT_IFRAME_MODE,
  OPERATION_STATE,
  TRANSACTIONS_STATE,
  TRANSACTIONS_TYPE,
} from 'api/transactions/constants';
import { ANTIFRAUD_MODE } from 'routes/pages/AuthorizedStack/Merchants/Merchant/Form/helpers/constants';
import { TObject } from 'utils/types';

export const normalizeReconFilters = (data: IReconFilters): IReconFilters => {
  return {
    name: (data?.name as string) || '',
    operation: (data?.operation as string[]) || [],
  };
};

export const normalizeDictionaries = (data: TDictionaryType): IDictionaries => {
  const paymentMethodMap: Record<string, string> = {};
  const paymentMethod =
    data?.paymentMethod?.map((method) => {
      const name = (method?.name as string) || '';
      const description = (method?.description as string) || '';
      if (name) {
        paymentMethodMap[name] = description;
      }
      return { description, name };
    }) || [];

  const resultCodeMap: Record<string, IResultCode> = {};
  const resultCode =
    data?.resultCode?.map((codeData) => {
      const resultCode = {
        code: (codeData?.code as string) || '',
        description: (codeData?.description as string) || '',
        message: (codeData?.message as string) || '',
      };
      resultCodeMap[resultCode.code] = resultCode;
      return resultCode;
    }) || [];

  const customParameterNames =
    data?.customParameterNames?.map((parameter) => {
      const name = (parameter?.name as string) || '';
      const value = (parameter?.value as string) || '';

      return { name, value };
    }) || [];

  return {
    adapterCode: (data?.adapterCode as string[]) || [],
    antifraudFinalTransactionState:
      (data?.antifraudFinalTransactionState as TRANSACTIONS_STATE[]) || [],
    antifraudMode: (data?.antifraudMode as ANTIFRAUD_MODE[]) || [],
    antifraudProvider: (data?.antifraudProvider as string[]) || [],
    apiAccessMode: (data?.apiAccessMode as API_ACCESS_MODE[]) || [],
    auditOperationType: (data?.auditOperationType as string[]) || [],
    auditTargetType: (data?.auditTargetType as string[]) || [],
    billingContractType: (data?.billingContractType as string[]) || [],
    binGroupType: (data?.binGroupType as string[]) || [],
    builderAdapterCode: (data?.builderAdapterCode as string[]) || [],
    cardBrand: (data?.cardBrand as string[]) || [],
    cardholderOverrideTypes: (data?.cardholderOverrideTypes as string[]) || [],
    cardTokenVerificationStatus: (data?.cardTokenVerificationStatus as string[]) || [],
    cardTypes: (data?.cardTypes as string[]) || [],
    cashierTheme: (data?.cashierTheme as string[]) || [],
    chargebackState: (data?.chargebackState as CHARGEBACK_STATE[]) || [],
    clientRequiredFields: (data?.clientRequiredFields as string[]) || [],
    countryCode: (data?.countryCode as string[]) || [],
    countryGroupType: (data?.countryGroupType as string[]) || [],
    cpmPaymentInstruments: (data?.cpmPaymentInstruments as string[]) || [],
    currency: (data?.currency as string[]) || [],
    customActionStage: (data?.customActionStage as string[]) || [],
    customActionTriggerType: (data?.customActionTriggerType as string[]) || [],
    customActionType: (data?.customActionType as string[]) || [],
    customerIdNormalization: (data?.customerIdNormalization as string[]) || [],
    customParamConfigGroupEditMode:
      (data?.customParamConfigGroupEditMode as string[]) || [],
    customParameterInputClasses: (data?.customParameterInputClasses as string[]) || [],
    customParameterInputTypes: (data?.customParameterInputTypes as string[]) || [],
    customParameterNames,
    customParameterTypes: (data?.customParameterTypes as string[]) || [],
    customParamsDepositMode: (data?.customParamsDepositMode as string[]) || [],
    customParamsPayoutMode: (data?.customParamsPayoutMode as string[]) || [],
    dataType: (data?.dataType as string[]) || [],
    exitIFrameMode: (data?.exitIFrameMode as EXIT_IFRAME_MODE[]) || [],
    exitIFrameModes: (data?.exitIFrameModes as string[]) || [],
    finalTransactionState: (data?.finalTransactionState as string[]) || [],
    forcedOverrideTypes: (data?.forcedOverrideTypes as string[]) || [],
    forcedPreparePageTypes: (data?.forcedPreparePageTypes as string[]) || [],
    hqLegalEntityFileSubType: (data?.hqLegalEntityFileSubType as string[]) || [],
    limitCountOf: (data?.limitCountOf as string[]) || [],
    limitPeriodCalculationType:
      (data?.limitPeriodCalculationType as CALCULATION_VARIANTS[]) || [],
    limitPeriodType: (data?.limitPeriodType as PERIOD_TYPE_VARIANTS[]) || [],
    limitTransactionStatus: (data?.limitTransactionStatus as string[]) || [],
    limitType: (data?.limitType as string[]) || [],
    merchantStatus: (data?.merchantStatus as ORGANIZATIONS_STATUS[]) || [],
    merchantType: (data?.merchantType as ORGANIZATIONS_TYPE[]) || [],
    minTurnoverFeeModeType: (data?.minTurnoverFeeModeType as string[]) || [],
    mtCaActionType: (data?.mtCaActionType as string[]) || [],
    mtCaFeature: (data?.mtCaFeature as string[]) || [],
    mtCaTriggerType: (data?.mtCaTriggerType as string[]) || [],
    notificationChannelType: (data?.notificationChannelType as string[]) || [],
    notificationMessageState: (data?.notificationMessageState as string[]) || [],
    notificationTypes: (data?.notificationTypes as string[]) || [],
    operationState: (data?.operationState as OPERATION_STATE[]) || [],
    operationType: (data?.operationType as string[]) || [],
    parameterLevel: (data?.parameterLevel as string[]) || [],
    paymentMethod,
    paymentMethodMap,
    paymentPageTheme: (data?.paymentPageTheme as string[]) || [],
    priorityType: (data?.priorityType as string[]) || [],
    reconAcceptanceModes: (data?.reconAcceptanceModes as string[]) || [],
    reconciliationStatus: (data?.reconciliationStatus as string[]) || [],
    reconciliationTransactionStates:
      (data?.reconciliationTransactionStates as string[]) || [],
    reconFilters:
      (data?.reconFilters as IReconFilters[])?.map(normalizeReconFilters) || [],
    resultCode,
    resultCodeMap,
    rollingReserveType: (data?.rollingReserveType as string[]) || [],
    separatorType: (data?.separatorType as string[]) || [],
    settlementContractPeriodType:
      (data?.settlementContractPeriodType as SETTLEMENT_CONTRACT_PERIOD_TYPE[]) || [],
    settlementFeeMode: (data?.settlementFeeMode as FEE_MODE[]) || [],
    settlementTransactionStates: (data?.settlementTransactionStates as string[]) || [],
    settlementTransactionTypes: (data?.settlementTransactionTypes as string[]) || [],
    settlementType: (data?.settlementType as SETTLEMENT_TYPE[]) || [],
    transactionState: (data?.transactionState as TRANSACTIONS_STATE[]) || [],
    transactionStates: (data?.transactionStates as TRANSACTIONS_STATE[]) || [],
    transactionType: (data?.transactionType as TRANSACTIONS_TYPE[]) || [],
    transactionTypes: (data?.transactionTypes as TRANSACTIONS_TYPE[]) || [],
    truncateOptionTypes: (data?.truncateOptionTypes as string[]) || [],
    uniqueCustomerIdType: (data?.uniqueCustomerIdType as string[]) || [],
  };
};

export const normalizeRoles = (data: TObject[]): IRole[] => {
  return data?.map((roleData) => ({
    permissions: roleData?.permissions as PERMISSIONS[],
    role: (roleData?.role as string) || '',
  }));
};

export const normalizeNotificationFilters = (data: INotificationFilters) => {
  return {
    name: (data?.name as string) || '',
    notificationFilterViewEnabled:
      typeof data?.notificationFilterViewEnabled === 'undefined'
        ? true
        : data?.notificationFilterViewEnabled,
    values: (data?.values as string[]) || [],
  };
};

export const normalizeAnalyticsDictionaries = (
  data: TAnalyticDictionaryType,
): IAnalyticDictionaries => {
  const templateValues: Record<string, string[]> = {};
  const graphViewTypes: Record<string, string[]> = {};

  Object.entries(data?.templateValues).forEach(([key, value]) => {
    templateValues[key] = value;
  });

  Object.entries(data?.graphViewTypes).forEach(([key, value]) => {
    graphViewTypes[key] = value;
  });

  return {
    betweenMetricTypes: (data?.betweenMetricTypes as string[]) || [],
    channelTypes: (data?.channelTypes as string[]) || [],
    comparisonTypes: (data?.comparisonTypes as COMPARISON_TYPES[]) || [],
    dayOfWeeks: (data?.dayOfWeeks as string[]) || [],
    deviationTypes: (data?.deviationTypes as DEVIATION_TYPES[]) || [],
    evaluationFrequencies: (data?.evaluationFrequencies as string[]) || [],
    graphTypes: (data?.graphTypes as string[]) || [],
    graphViewTypes,
    levels: (data?.levels as string[]) || [],
    notificationFilters: data?.notificationFilters.map(normalizeNotificationFilters),
    notificationTypes: (data?.notificationTypes as NOTIFICATION_TYPES[]) || [],
    operations: (data?.operations as string[]) || [],
    pieChartTypes: (data?.pieChartTypes as string[]) || [],
    templateValues,
    transactionStatuses: (data?.transactionStatuses as string[]) || [],
  };
};

export const normalizeAuthDictionaries = (data: TObject): IAuthDictionaries => {
  return {
    userActivityActionTypes: (data?.userActivityActionTypes as string[]) || [],
    userActivityStatuses: (data?.userActivityStatuses as string[]) || [],
  };
};

export const normalizeBuilderDictionaries = (data: TObject): IBuilderDictionaries => {
  return {
    checkStatusResultKeys: (data?.checkStatusResultKeys as string[]) || [],
    customerTokenSources: (data?.customerTokenSources as string[]) || [],
    hashTypes: (data?.hashTypes as string[]) || [],
    hmacHashTypes: (data?.hmacHashTypes as string[]) || [],
    httpMethods: (data?.httpMethods as string[]) || [],
    payoutResultKeys: (data?.payoutResultKeys as string[]) || [],
    redirectModes: (data?.redirectModes as string[]) || [],
    responseBodyTypes: (data?.responseBodyTypes as string[]) || [],
    sendPaymentResultKeys: (data?.sendPaymentResultKeys as string[]) || [],
    signTypes: (data?.signTypes as string[]) || [],
    stateAfterRedirectTypes: (data?.stateAfterRedirectTypes as string[]) || [],
    templateDataParams: (data?.templateDataParams as string[]) || [],
    transactionTypes: (data?.transactionTypes as string[]) || [],
    webhookBodyTypes: (data?.webhookBodyTypes as string[]) || [],
    webhookResultKeys: (data?.webhookResultKeys as string[]) || [],
  };
};
